import { all, call, delay, fork, put, select, takeEvery } from 'redux-saga/effects';
import api from '../../utils/api';
import { SearchActionTypes } from './common.types';
import { addApiError, clearApiErrors } from './errors.slice';
import loadingSlice from './loading.slice';
import searchSlice from './search.slice';
import stoneAnalytics from '@stone/utils/analytics';
const SEARCH = 'SEARCH';
const overlayClass = 'overlay';
const noOverflow = 'no-overflow';
function* handleToggleSearch(action) {
    var _a, _b, _c, _d;
    try {
        const { apiErrors } = yield select((state) => state.errors);
        if (apiErrors.length > 0) {
            yield put(clearApiErrors());
        }
        if (action.payload) {
            (_a = document.body) === null || _a === void 0 ? void 0 : _a.classList.add(overlayClass);
            (_b = document.body) === null || _b === void 0 ? void 0 : _b.classList.add(noOverflow);
        }
        else {
            (_c = document.body) === null || _c === void 0 ? void 0 : _c.classList.remove(overlayClass);
            (_d = document.body) === null || _d === void 0 ? void 0 : _d.classList.remove(noOverflow);
            yield delay(150); // To make the animation smoother
        }
        yield put(searchSlice.actions.updateSearchVisibility(action.payload));
    }
    catch (err) {
        yield put(addApiError({
            code: 'UNKNOWN',
            metadata: { location: 'handleToggleSearch' },
        }));
    }
}
function* handleSearchInput(action) {
    try {
        const { apiErrors } = yield select((state) => state.errors);
        if (apiErrors.length > 0) {
            yield put(clearApiErrors());
        }
        yield put(searchSlice.actions.updateInput(action.payload));
    }
    catch (err) {
        yield put(addApiError({
            code: 'UNKNOWN',
            metadata: { location: 'handleSearchInput' },
        }));
    }
}
function* handleSearchQuery(action) {
    try {
        const { apiErrors } = yield select((state) => state.errors);
        if (apiErrors.length > 0) {
            yield put(clearApiErrors());
        }
        const { inputSearch } = yield select((state) => state.search);
        yield put(searchSlice.actions.updateQuery(inputSearch));
    }
    catch (err) {
        yield put(addApiError({
            code: 'UNKNOWN',
            metadata: { location: 'handleSearchQuery' },
        }));
    }
}
function* handleResetSearch(action) {
    try {
        const { apiErrors } = yield select((state) => state.errors);
        if (apiErrors.length > 0) {
            yield put(clearApiErrors());
        }
        yield put(searchSlice.actions.resetSearch(action.payload));
    }
    catch (err) {
        yield put(addApiError({
            code: 'UNKNOWN',
            metadata: { location: 'handleResetSearch' },
        }));
    }
}
function* handleSearchFetch(action) {
    var _a, _b;
    try {
        const { apiErrors } = yield select((state) => state.errors);
        if (apiErrors.length > 0) {
            yield put(clearApiErrors());
        }
        const { selectedFilters, selectedSort, inputSearch, filteredCategory, progress } = yield select((state) => state.search);
        const { start, resultsType, cancelToken } = action.payload;
        yield put(loadingSlice.actions.addLoadingItem(SEARCH));
        // console.info('%c[SEARCH]', 'color:green;', 'debounced fetch for:', inputSearch);
        // console.time('fetchProducts');
        const searchResults = yield call(api.searchProducts, {
            q: inputSearch,
            id: filteredCategory,
            selectedFilters,
            selectedSort,
            start,
            cancelToken,
        });
        // https://documentation.b2c.commercecloud.salesforce.com/DOC1/topic/com.demandware.dochelp/content/b2c_commerce/topics/third_party/b2c_analytics_reporting_integration_url_patterns.html
        api.reportingEventStart({
            ID: 'ProductSearch',
            Phrase: inputSearch,
            ResultCount: searchResults.count,
        });
        if (resultsType === 'INIT') {
            yield put(searchSlice.actions.initResults({
                data: searchResults,
                resultsType,
            }));
        }
        else {
            yield put(searchSlice.actions.updateResults({
                data: searchResults,
                resultsType,
            }));
        }
        //search analytics tracking (addded for Stone)
        if (window.brandId === 'ST' && ((_a = searchResults.products) === null || _a === void 0 ? void 0 : _a.length) > 0)
            stoneAnalytics.productSearchImpressions({ products: searchResults.products, index: searchResults.pageNumber !== 0 ? progress : 0 });
    }
    catch (err) {
        yield put(addApiError({
            code: (_b = action.payload) === null || _b === void 0 ? void 0 : _b.resultsType,
            metadata: { location: 'handleSearchFetch' },
        }));
    }
    yield put(loadingSlice.actions.clearLoadingItems());
}
function* handleSearchRefinement(action) {
    try {
        const { apiErrors } = yield select((state) => state.errors);
        if (apiErrors.length > 0) {
            yield put(clearApiErrors());
        }
        switch (action.payload.filterAction) {
            case 'ADD':
                yield put(searchSlice.actions.addFilters(action.payload));
                break;
            case 'CATEGORY':
                yield put(searchSlice.actions.addFilters(action.payload));
                break;
            case 'SORT':
                yield put(searchSlice.actions.updateSort(action.payload.selectedSort));
                break;
            case 'RESET':
                yield put(searchSlice.actions.resetFilter(action.payload));
                break;
            case 'REPLACE':
                yield put(searchSlice.actions.replaceFilters(action.payload));
                break;
        }
    }
    catch (err) {
        yield put(addApiError({
            code: 'UNKNOWN',
            metadata: { location: 'handleSearchRefinement' },
        }));
    }
    yield put(loadingSlice.actions.clearLoadingItems());
}
function* handleMenuData(action) {
    delete action.payload.getFormattedData;
    yield put(searchSlice.actions.updateMenuData(action.payload));
}
/**
 * @desc Watches every specified action and runs effect method and passes action args to it
 */
function* watchFetchRequest() {
    yield takeEvery(SearchActionTypes.TOGGLE_SEARCH, handleToggleSearch);
    yield takeEvery(SearchActionTypes.RESET_SEARCH, handleResetSearch);
    yield takeEvery(SearchActionTypes.INPUT_SEARCH, handleSearchInput);
    yield takeEvery(SearchActionTypes.FETCH_SEARCH, handleSearchFetch);
    yield takeEvery(SearchActionTypes.QUERY_SEARCH, handleSearchQuery);
    yield takeEvery(SearchActionTypes.UPDATE_SEARCH_REFINEMENT, handleSearchRefinement);
    yield takeEvery(SearchActionTypes.MENU_DATA, handleMenuData);
}
/**
 * @desc saga init, forks in effects, other sagas
 */
export default function* searchSaga() {
    yield all([fork(watchFetchRequest)]);
}
